import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { User as FirebaseUser } from 'firebase/auth';
import { nextBadgeNumber, isTimestampBadge } from './badgeNumber';
import { INVITE_ONBOARDING } from './featureFlags';

// Legacy single-company fallback: when invite onboarding is OFF, new users
// auto-join this company. When ON, new users get NO company (companyId '') and
// are routed to /join to redeem an invite — the multi-facility path.
const DEFAULT_COMPANY_ID = 'Oyxyl5p11Xh7MRrInrJp';
const PENDING_COMPANY_ID = ''; // no facility yet — gated to /join
const onboardingCompanyId = () => (INVITE_ONBOARDING ? PENDING_COMPANY_ID : DEFAULT_COMPANY_ID);

/**
 * Ensures that a user document exists in Firestore for the given Firebase user
 * Creates a basic document if one doesn't exist
 *
 * @param fbUser - The Firebase authentication user
 * @returns true if a document was created, false if it already existed
 */
export async function ensureUserDocument(fbUser: FirebaseUser): Promise<boolean> {
  const userRef = doc(db, 'users', fbUser.uid);

  try {
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      // Document doesn't exist, create it with default values
      const isAdmin = !INVITE_ONBOARDING && fbUser.email === 'thinkimaedu@gmail.com';

      await setDoc(userRef, {
        id: fbUser.uid,
        companyId: onboardingCompanyId(), // '' when invite-onboarding is on -> /join
        email: fbUser.email,
        name: fbUser.displayName || fbUser.email?.split('@')[0] || 'User',
        role: isAdmin ? 'admin' : 'rbt', // Default role for new users
        status: 'active',
        badgeNumber: isAdmin ? 'ADMIN-001' : await nextBadgeNumber(),
        department: isAdmin ? 'Administration' : 'General',
        createdAt: Timestamp.fromDate(new Date()),
        lastLogin: Timestamp.fromDate(new Date()),
      });

      return true;
    } else {
      const data = userSnap.data();
      const isAdmin = !INVITE_ONBOARDING && fbUser.email === 'thinkimaedu@gmail.com';

      // Migrate legacy timestamp badges (USER-<epoch>) to a clean sequential
      // badge, but keep any already-clean badge (RBT-001, SPR-001, ...).
      if (isTimestampBadge(data.badgeNumber)) {
        const migratedBadge = isAdmin ? 'ADMIN-001' : await nextBadgeNumber();
        await setDoc(
          userRef,
          { badgeNumber: migratedBadge, updatedAt: Timestamp.fromDate(new Date()) },
          { merge: true }
        );
        data.badgeNumber = migratedBadge;
      }

      // Document exists, but check if it has required fields
      if (!data.companyId || !data.role || !data.status) {
        // Merge with existing data to preserve any existing fields
        await setDoc(userRef, {
          ...data,
          id: fbUser.uid,
          companyId: data.companyId || onboardingCompanyId(),
          email: data.email || fbUser.email,
          name: data.name || fbUser.displayName || fbUser.email?.split('@')[0] || 'User',
          role: data.role || (isAdmin ? 'admin' : 'rbt'),
          status: data.status || 'active',
          badgeNumber: data.badgeNumber || (isAdmin ? 'ADMIN-001' : await nextBadgeNumber()),
          department: data.department || (isAdmin ? 'Administration' : 'General'),
          updatedAt: Timestamp.fromDate(new Date()),
        }, { merge: true });

        return true;
      }

      return false;
    }
  } catch (error) {
    console.error('ensureUserDocument: Error ensuring user document:', error);
    throw error;
  }
}
